import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IImageReaction, IReactionSet, ReactionsByImageId, ReactionsStatus } from 'types/reactions';

export interface IReactionsState {
  // null means reactions are switched off for this gallery.
  set: IReactionSet | null;
  byImageId: ReactionsByImageId;
  status: ReactionsStatus;
  // Keyed `${imageId}:${slot}` — guards double taps and scopes rollback.
  pendingKeys: Record<string, true>;
  bulkStatus: 'idle' | 'saving';
}

const initialState: IReactionsState = {
  set: null,
  byImageId: {},
  status: 'idle',
  pendingKeys: {},
  bulkStatus: 'idle'
};

export const reactionKey = (imageId: string, slot: number) => `${imageId}:${slot}`;

// An entry that falls to zero is dropped, not left at 0: a lingering zero would match the reaction
// filter and paint a `0` on the tile.
const shiftReaction = (
  state: IReactionsState,
  imageId: string,
  slot: number,
  direction: 1 | -1,
  template?: Pick<IImageReaction, 'kind' | 'value' | 'filled'>
) => {
  const existing = state.byImageId[imageId] || [];
  const entry = existing.find((reaction) => reaction.slot === slot);

  if (!entry) {
    if (direction === -1 || !template) return;

    state.byImageId[imageId] = [...existing, { slot, ...template, count: 1, isOwn: true }];

    return;
  }

  entry.count = Math.max(0, entry.count + direction);
  entry.isOwn = direction === 1;

  if (entry.count > 0) return;

  const remaining = existing.filter((reaction) => reaction.slot !== slot);

  if (remaining.length) state.byImageId[imageId] = remaining;
  else delete state.byImageId[imageId];
};

const reactionsSlice = createSlice({
  name: 'reactions',
  initialState,
  reducers: {
    setReactionsStatus(state, { payload }: PayloadAction<ReactionsStatus>) {
      state.status = payload;
    },
    setReactionsData(
      state,
      { payload }: PayloadAction<{ set: IReactionSet | null; images: ReactionsByImageId }>
    ) {
      state.set = payload.set;
      state.byImageId = payload.images;
      state.status = 'succeeded';
    },
    applyLocalReaction(
      state,
      {
        payload
      }: PayloadAction<{
        imageIds: string[];
        slot: number;
        direction: 1 | -1;
        template?: Pick<IImageReaction, 'kind' | 'value' | 'filled'>;
      }>
    ) {
      payload.imageIds.forEach((imageId) =>
        shiftReaction(state, imageId, payload.slot, payload.direction, payload.template)
      );
    },
    setReactionPending(state, { payload }: PayloadAction<{ key: string; pending: boolean }>) {
      if (payload.pending) state.pendingKeys[payload.key] = true;
      else delete state.pendingKeys[payload.key];
    },
    setBulkStatus(state, { payload }: PayloadAction<IReactionsState['bulkStatus']>) {
      state.bulkStatus = payload;
    },
    resetReactions: () => initialState
  }
});

export const {
  setReactionsStatus,
  setReactionsData,
  applyLocalReaction,
  setReactionPending,
  setBulkStatus,
  resetReactions
} = reactionsSlice.actions;

export const reactionsReducer = reactionsSlice.reducer;
